import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";

import PageHeader from "../../components/common/PageHeader";
import CreateCampaignForm from "../../components/brand/CreateCampaignForm";
import { campaignService } from "../../services/campaign.service";
import { convertCampaignToFormData } from "@/lib/campaign-form";
import type { Campaign, CampaignFormData } from "../../types/campaign";

function FormSkeleton() {
  return (
    <div className="max-w-3xl space-y-4">
      {[3, 2, 2].map((fields, i) => (
        <div key={i} className="rounded-lg border border-border bg-card">
          <div className="px-5 pt-4 pb-3 animate-pulse">
            <div className="h-4 w-24 rounded bg-muted" />
          </div>
          <div className="space-y-5 px-5 pb-5 animate-pulse">
            {Array.from({ length: fields }).map((_, j) => (
              <div key={j} className="space-y-2">
                <div className="h-3.5 w-28 rounded bg-muted" />
                <div className="h-9 w-full rounded-lg bg-muted" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const SCAN_PAGE_SIZE = 100;
const SCAN_MAX_PAGES = 5;

/**
 * One campaign by id. Older API deployments have no single-campaign read,
 * so after trying it the list is scanned, active first.
 */
async function findCampaign(id: string): Promise<Campaign | null> {
  try {
    const campaign = await campaignService.getCampaignById(id);
    if (campaign?.id === id) return campaign;
  } catch {
    // fall through to the list scan
  }
  for (const status of ["ACTIVE", "INACTIVE"]) {
    for (let page = 1; page <= SCAN_MAX_PAGES; page += 1) {
      const response = await campaignService.getCampaigns(page, SCAN_PAGE_SIZE, status);
      const found = response.data?.find((c) => c.id === id);
      if (found) return found;
      if (page >= (response.pagination?.totalPages ?? 1)) break;
    }
  }
  return null;
}

/** /brand/campaigns/new and /brand/campaigns/:campaignId/edit */
export default function CampaignFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const isEdit = Boolean(campaignId);
  // The list page hands the row over so editing needs no extra request.
  const handedOver = (location.state as { campaign?: Campaign } | null)?.campaign;
  const [initialData, setInitialData] = useState<CampaignFormData | null>(() =>
    handedOver && handedOver.id === campaignId ? convertCampaignToFormData(handedOver) : null
  );
  const [loading, setLoading] = useState(isEdit && !initialData);

  useEffect(() => {
    if (!campaignId || initialData) return;
    let cancelled = false;
    (async () => {
      try {
        const campaign = await findCampaign(campaignId);
        if (!campaign) throw new Error("Campaign not found");
        if (!cancelled) setInitialData(convertCampaignToFormData(campaign));
      } catch (error) {
        console.error("Failed to load campaign for editing:", error);
        toast.error("Could not load the campaign");
        navigate("/brand/campaigns", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, navigate]);

  const backToList = () => navigate("/brand/campaigns");

  return (
    <div className="space-y-4">
      <PageHeader title={isEdit ? "Edit campaign" : "New campaign"} closeTo="/brand/campaigns" />
      {loading ? (
        <FormSkeleton />
      ) : (
        <CreateCampaignForm
          key={campaignId ?? "new"}
          campaignId={campaignId}
          initialData={initialData ?? undefined}
          onSave={backToList}
          onCancel={backToList}
        />
      )}
    </div>
  );
}
